import { ChatCompletionRequestMessageRoleEnum } from "@app/interfaces/ChatCompletionRoleEnum";
import { ChatCompletionResultInterface } from "@app/interfaces/ChatCompletionResultInterface";
import {
    ProviderOptionsType
} from "@app/interfaces/ProviderOptionsInterface";
import { FinishReasonEnum } from "@app/models/FinishReasonEnum";
import {
    AdditionalCompletionRequestArgumentsInterface,
    ConversationMessageInterface,
    ModelCardInterface,
    ToolCallInterface,
    VisionPromptContentImageInterface,
    VisionPromptUtil
} from "@dashart/dashart-gpt-shared-library";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom, useSetAtom } from "jotai";
import { v4 as uuidv4 } from 'uuid';
import { ChatServiceInterface } from "../../../app/interfaces/ChatServiceInterface";
import { FileInfoInterface } from "../../../app/interfaces/FileInfoInterface";
import { availableModelsAtom, lastToastMessageAtom, lastUsedConversationIdAtom, selectedModelsByServiceNamesAtom } from "../../../app/models/AppStates";
import { ChatHistoryService } from "../../../app/services/ChatHistoryService";
import { ClaudeService } from "../../../app/services/ClaudeService";
import { ToolProgressInterface } from "../../../app/services/CustomEndpointChatService";
import { GeminiService } from "../../../app/services/GeminiService";
import { MistralService } from "../../../app/services/MistralService";
import { ModelCardService } from "../../../app/services/ModelCardService";
import { OpenAiService } from "../../../app/services/OpenAiService";
import { OpenRouterService } from "../../../app/services/OpenRouterService";
import FilePromptUtil from "../../../app/utils/FilePromptUtil";
import ChatScreen from "./ChatScreen";

const MAX_IMAGES = 5;

export interface ChatScreenHocProps {
    chatService: ChatServiceInterface;
}

const ChatScreenHoc: React.FunctionComponent<ChatScreenHocProps> = (props: ChatScreenHocProps) => {
    const { t } = useTranslation();

    const [instruction, setInstruction] = useState('');
    const [question, setQuestion] = useState('');
    const [images, setImages] = useState<VisionPromptContentImageInterface[]>([]);
    const [selectedTextFiles, setSelectedTextFiles] = useState<FileInfoInterface[] | null>(null);
    const [messages, setMessages] = useState<ConversationMessageInterface[]>([]);

    const [lastUsedConversationId, setLastUsedConversationId] = useAtom(lastUsedConversationIdAtom);
    const setLastToastMessage = useSetAtom(lastToastMessageAtom);

    const [maxTokens, setMaxTokens] = useState(null);
    const [temperature, setTemperature] = useState(null);
    const [availableModels, setAvailableModels] = useAtom<ModelCardInterface[]>(availableModelsAtom);
    const [selectedModelsByServiceNames, setSelectedModelsByServiceNames] = useAtom<{
        [key: string]: ModelCardInterface,
    }>(selectedModelsByServiceNamesAtom);

    const [extendedModelCard, setExtendedModelCard] = useState<ModelCardInterface | null>(null);

    // Default provider options
    const [providerOptions, setProviderOptions] = useState<{
        [key: string]: ProviderOptionsType
    }>({
        anthropic: {
            provider: 'anthropic',
            webSearch: {
                active: false,
                maxTries: 3
            },
            reasoning: {
                active: false,
                maxTokens: 2000
            },
            toolUse: false
        },
        openai: {
            provider: 'openai',
            jsonMode: false
        },
        mistral: {
            provider: 'mistral',
            safePrompt: false
        },
        google: {
            provider: 'google'
        },
        openrouter: {
            provider: 'openrouter'
        }
    });

    const [completionState, setCompletionState] = useState<{
        running: boolean;
        currentQuestion: string | null;
        currentCompletion: string | null;
        currentReasoning: string | null;
        currentToolCalls: ToolCallInterface[] | null;
        currentToolProgress: ToolProgressInterface[] | null;
        error: string | null;
        continueOutputAvailable: boolean;
        conversationId: string;
    }>({
        running: false,
        currentQuestion: null,
        currentCompletion: null,
        currentReasoning: null,
        currentToolCalls: null,
        currentToolProgress: null,
        error: null,
        continueOutputAvailable: false,
        conversationId: uuidv4(),
    });

    /**
     * Get the current provider based on the chat service
     */
    const getCurrentProvider = (): "openai" | "anthropic" | "mistral" | "google" | "openrouter" => {
        const baseNameStart = props.chatService.modelBaseNameStart;

        switch (baseNameStart) {
            case 'gpt':
                return 'openai';
            case 'claude':
                return 'anthropic';
            case 'mistral':
                return 'mistral';
            case 'gemini':
                return 'google';
            case 'openrouter':
                return 'openrouter';
            default:
                return 'openai';
        }
    };

    /**
     * Set images and validate them, remove images that do not have an imageUrl or content
     * @param images 
     */
    const setAndValidateImages = React.useCallback((images: VisionPromptContentImageInterface[]) => {
        images = images.filter((image) => {
            return image && image.imageUrl;
        });

        // limit length to 5
        if (images.length > MAX_IMAGES) {
            images = images.slice(0, MAX_IMAGES);

            setLastToastMessage({
                severity: "error",
                summary: t(
                    "chat_screen.max_images_reached",
                    {
                        maxImages: MAX_IMAGES,
                        interpolation: { escapeValue: false }
                    }
                ),
            });
        }

        setImages(images);
    }, [images]);

    useEffect(() => {
        // Clear stale models from previous service immediately, before the async fetch resolves.
        // Without this, persisted models from a different provider remain visible until the new
        // fetch completes — on Android PWA this window is long enough to always show wrong models.
        setAvailableModels([]);

        // load models
        props.chatService.getAvailableChatModels().then((models) => {
            setAvailableModels(models)
        });

        props.chatService.requestNewConversationId().then((newConversationId) => {
            setCompletionState(
                (prevState) => {
                    setLastUsedConversationId(prevState.conversationId);
                    return {
                        ...prevState,
                        conversationId: newConversationId,
                    }
                }
            )
        });

    }, [props.chatService]);

    useEffect(() => {
        const modelId = selectedModelsByServiceNames[props.chatService.modelBaseNameStart]?.id;

        if (modelId) {
            let prefix = '';

            // we use a generic model card service, so we need to prepend the model provider to the model id
            switch (props.chatService.modelBaseNameStart) {
                case OpenAiService.getInstance().modelBaseNameStart:
                    prefix = 'openai/';
                    break;
                case ClaudeService.getInstance().modelBaseNameStart:
                    prefix = 'anthropic/';
                    break;
                case MistralService.getInstance().modelBaseNameStart:
                    prefix = 'mistralai/';
                    break;
                case GeminiService.getInstance().modelBaseNameStart:
                    prefix = 'google/';
                    break;
                case OpenRouterService.getInstance().modelBaseNameStart:
                    // for open router we do not need a prefix, because the model ids already include the provider
                    prefix = '';
                    break;
            }

            // as initial basic model card we use the selected model by service name
            setExtendedModelCard(selectedModelsByServiceNames[props.chatService.modelBaseNameStart]);

            // then we try to load the extended model card
            ModelCardService.getInstance().getModelById(`${prefix}${modelId}`).then((model) => {
                setExtendedModelCard(model);
            }).catch((error) => {
                console.error(error);
            });
        }
    }, [selectedModelsByServiceNames, props.chatService]);

    /**
     * Select the first model as default, as soon as all models are loaded, but only in case not already selected.
     * Also syncs fresh capability flags (e.g. supportsVision) onto an already-selected /
     * persisted model card so stale persisted state cannot hide UI controls.
     */
    useEffect(() => {
        const key = props.chatService.modelBaseNameStart;
        const current = selectedModelsByServiceNames[key];

        if (!current && availableModels.length) {
            setSelectedModelsByServiceNames({
                ...selectedModelsByServiceNames,
                [key]: availableModels[0]
            });
            return;
        }

        if (current && availableModels.length) {
            const fresh = availableModels.find((m) => m.id === current.id);
            if (fresh && fresh.supportsVision !== current.supportsVision) {
                setSelectedModelsByServiceNames({
                    ...selectedModelsByServiceNames,
                    [key]: { ...current, supportsVision: fresh.supportsVision },
                });
            }
        }
    }, [selectedModelsByServiceNames, availableModels]);

    /**
     * Create a new conversation
     */
    const newConversationClickHandler = async () => {
        createNewConversation();
    }

    /**
     * Fork conversation with all messages until provided message index
     * @param messageIndex 
     */
    const forkMessage = async (messageIndex: number) => {
        createNewConversation(
            messages.slice(0, messageIndex + 1)
        );
    }

    /**
     * Create a new conversation and stop the current running completion, if any
     * initialize with provided messages
     * @param initialMessages 
     */
    const createNewConversation = async (initialMessages: ConversationMessageInterface[] = []) => {
        // cancel current completion
        props.chatService.cancelCurrentCompletion();

        // request new conversation id
        const newConversationId = await props.chatService.requestNewConversationId();

        // clear messages
        setMessages(initialMessages);
        // clear instruction
        setInstruction('');
        // clear question
        setQuestion('');
        // clear images
        setImages([]);
        // clear files
        setSelectedTextFiles(null);

        // notify about new conversation id
        setLastUsedConversationId(
            completionState.conversationId
        );

        // reset state
        setCompletionState(
            {
                running: false,
                currentQuestion: '',
                currentCompletion: null,
                currentReasoning: null,
                currentToolCalls: null,
                currentToolProgress: null,
                error: null,
                continueOutputAvailable: false,
                conversationId: newConversationId,
            }
        )
    }

    /**
     * Continue with current output
     */
    const continueClickHandler = async () => {
        submitHandler("continue output");
    }

    /**
     * Trigger chat completion
     *
     * If command is set, it will be used as question
     *
     *
     * @param command
     */
    const submitHandler = async (command?: string) => {
        let userQuestion = command ? command : question;

        // attachments (images or text files) can be sent without a prompt
        const isAttachmentsOnly = !userQuestion;

        // filter out images that do not have an imageUrl nor content
        const imagesForPrompt = (images ?? []).filter((image) => {
            return image && image.imageUrl;
        });

        // if we have no prompt and no attachments, return and trigger toast
        if (isAttachmentsOnly && (!(selectedTextFiles && selectedTextFiles.length > 0) && imagesForPrompt.length === 0)) {
            setLastToastMessage({
                severity: "error",
                summary: t("chat_screen.no_prompt"),
            });
            return;
        }

        // if text content files are set, append them to the prompt
        if (selectedTextFiles && selectedTextFiles.length > 0) {
            userQuestion = FilePromptUtil.appendTextFilesToPrompt(userQuestion, selectedTextFiles);

            // when submitting attachments only, remove the leading file separator
            if (isAttachmentsOnly) {
                userQuestion = userQuestion.trimStart();
            }
        }

        if (imagesForPrompt.length > 0) {
            // for images we have to combine prompt and image
            userQuestion = VisionPromptUtil.convertVisionPromptToString(userQuestion, imagesForPrompt);
        }

        // Handle the submission logic here (e.g., fetch answer from an API)

        setCompletionState(
            (prevState) => {
                return {
                    running: true,
                    currentQuestion: userQuestion,
                    currentCompletion: null,
                    currentReasoning: null,
                    currentToolCalls: null,
                    currentToolProgress: null,
                    error: null,
                    continueOutputAvailable: false,
                    conversationId: prevState.conversationId,
                }
            }
        )

        props.chatService.chatCompletion(
            userQuestion,
            false,
            [
                ...instruction ? [
                    {
                        message: {
                            role: ChatCompletionRequestMessageRoleEnum.System,
                            content: instruction,
                        }
                    } as ConversationMessageInterface
                ] : [],
                ...messages.filter((message) => {
                    // Ignore empty messages unless they carry tool data that must
                    // be preserved for history and tool continuation.
                    const content = (message.message.content ?? '').trim();
                    const hasToolData = !!((message.message as any)?.tool_calls?.length) || !!(message.message as any)?.tool_call_id;
                    return content !== '' || hasToolData;
                }).map((messageContent) => {
                    const message = {
                        role: messageContent.message.role,
                        content: messageContent.message.content,
                    } as any;
                    (messageContent.message as any)?.tool_calls?.length && (message.tool_calls = (messageContent.message as any).tool_calls);
                    (messageContent.message as any)?.tool_call_id && (message.tool_call_id = (messageContent.message as any).tool_call_id);
                    (messageContent.message as any)?.tool_progress?.length && (message.tool_progress = (messageContent.message as any).tool_progress);
                    // Carry persisted stats forward so they survive subsequent saveConversation calls
                    (messageContent.message as any)?.stats && (message.stats = (messageContent.message as any).stats);
                    return { message } as ConversationMessageInterface
                })
            ],
            (data, prompt, currentCompletion, currentReasoning) => {
                const aggregatedToolCalls = (data as any)?.aggregatedToolCalls as ToolCallInterface[] | undefined;
                const toolProgress = (data as any)?.toolProgress as ToolProgressInterface[] | undefined;
                setCompletionState(
                    (prevState) => {
                        return {
                            ...prevState,
                            currentCompletion: currentCompletion,
                            currentReasoning: currentReasoning,
                            currentToolCalls: aggregatedToolCalls ?? prevState.currentToolCalls,
                            currentToolProgress: toolProgress ?? prevState.currentToolProgress,
                        }
                    }
                )
            },
            (data: ChatCompletionResultInterface, prompt, currentCompletion, currentReasoning) => {
                let canBeContinued = false;
                switch (data?.choices[0]?.finish_reason) {
                    case FinishReasonEnum.length:
                        canBeContinued = true;
                        break;
                    default:
                        canBeContinued = false;
                }

                const aggregatedToolCalls = (data as any)?.aggregatedToolCalls as ToolCallInterface[] | undefined;
                const toolProgress = (data as any)?.toolProgress as ToolProgressInterface[] | undefined;

                setCompletionState(
                    (prevState) => {
                        return {
                            running: false,
                            currentQuestion: null,
                            currentCompletion: null,
                            currentReasoning: null,
                            currentToolCalls: null,
                            currentToolProgress: null,
                            error: null,
                            continueOutputAvailable: canBeContinued,
                            conversationId: prevState.conversationId,
                        }
                    }
                )

                // only if question does not differ from initial prompt
                setQuestion(
                    (currentDisplayedQuestion) => {
                        const isVisionRequest = VisionPromptUtil.isVisionRequest(prompt);
                        // if it is a vision request, we need to validate the prompt against the current displayed question,
                        // if it is not a vision request, we can directly compare the prompt with the current displayed question
                        let currentPromptToBeValidated = isVisionRequest ? VisionPromptUtil.convertVisionPromptToContent(prompt)?.prompt ?? prompt : prompt;

                        if (selectedTextFiles && selectedTextFiles.length > 0) {
                            // remove files from prompt
                            currentPromptToBeValidated = FilePromptUtil.convertToPromptWithoutFiles(currentPromptToBeValidated, selectedTextFiles);
                        }

                        if (currentDisplayedQuestion.trim() === currentPromptToBeValidated.trim()) {
                            // reset the images too
                            setImages([]);
                            // clear files
                            setSelectedTextFiles(null);
                            return '';
                        } else {
                            return currentDisplayedQuestion;
                        }
                    }
                );

                setMessages((prevMessages) => [
                    ...prevMessages,
                    { message: {
                        role: ChatCompletionRequestMessageRoleEnum.User,
                        content: prompt
                    } },
                    { message: {
                        role: ChatCompletionRequestMessageRoleEnum.Assistant,
                        content: currentCompletion,
                        reasoning: currentReasoning,
                        ...(aggregatedToolCalls?.length ? { tool_calls: aggregatedToolCalls } : {}),
                        ...(toolProgress?.length ? { tool_progress: toolProgress } : {}),
                        ...(data?.stats ? { stats: data.stats } : {})
                    } as any }
                ]);
            },
            selectedModelsByServiceNames[props.chatService.modelBaseNameStart]?.id,
            maxTokens,
            completionState.conversationId,
            temperature,
            {
                customProviderRequestArguments: providerOptions[getCurrentProvider()]
            } as AdditionalCompletionRequestArgumentsInterface
        ).catch((error) => {
            setCompletionState(
                (prevState) => {
                    return {
                        running: false,
                        currentQuestion: null,
                        currentCompletion: null,
                        currentReasoning: null,
                        currentToolCalls: null,
                        currentToolProgress: null,
                        error: error?.message ?? 'unknown error',
                        continueOutputAvailable: false,
                        conversationId: prevState.conversationId,
                    }
                }
            )
        });
    };

    /**
     * Cancel the current completion
     */
    const cancelHandler = () => {
        props.chatService.cancelCurrentCompletion();

        setCompletionState(
            (prevState) => {

                // save current message state
                setMessages((prevMessages) => [
                    ...prevMessages,
                    { message: {
                        role: ChatCompletionRequestMessageRoleEnum.User,
                        content: prevState.currentQuestion
                    } },
                    { message: {
                        role: ChatCompletionRequestMessageRoleEnum.Assistant,
                        content: prevState.currentCompletion,
                        reasoning: prevState.currentReasoning,
                        ...(prevState.currentToolCalls?.length ? { tool_calls: prevState.currentToolCalls } : {}),
                        ...(prevState.currentToolProgress?.length ? { tool_progress: prevState.currentToolProgress } : {})
                    } }
                ]);

                // reset state
                return {
                    running: false,
                    currentQuestion: null,
                    currentCompletion: null,
                    currentReasoning: null,
                    currentToolCalls: null,
                    currentToolProgress: null,
                    error: null,
                    continueOutputAvailable: false,
                    conversationId: prevState.conversationId,
                }
            }
        )
    }

    /**
     * Load a conversation from history
     * @param conversationId 
     */
    const loadConversationFromHistory = async (conversationId: string, model: string): Promise<void> => {
        // load history
        try {
            const history = await ChatHistoryService.getInstance().getConversationHistory(conversationId);
            const instruction = history?.messages.find((historyEntry) => {
                return historyEntry.message.role === ChatCompletionRequestMessageRoleEnum.System;
            });

            // clear images
            setImages([]);

            setInstruction(instruction?.message.content);

            setMessages(history?.messages
                .filter((historyEntry) => {
                    // avoid system messages
                    return historyEntry.message.role !== ChatCompletionRequestMessageRoleEnum.System;
                })
                .map((historyEntry) => {
                    return {
                        message: {
                            role: historyEntry.message.role,
                            content: historyEntry.message.content,
                            reasoning: historyEntry.message.reasoning,
                            ...(historyEntry.message.tool_calls?.length ? { tool_calls: historyEntry.message.tool_calls } : {}),
                            ...(historyEntry.message.tool_call_id ? { tool_call_id: historyEntry.message.tool_call_id } : {}),
                            ...(historyEntry.message.tool_progress?.length ? { tool_progress: historyEntry.message.tool_progress } : {}),
                            // Persisted per-message completion stats (tokens, tok/s)
                            ...(historyEntry.message.stats ? { stats: historyEntry.message.stats } : {}),
                        }
                    }
                }));

            const chatServiceModel = availableModels.find((m) => m.id === model);
            console.log('loadConversationFromHistory 2', {
                'model': model,
                'service': props.chatService.modelBaseNameStart,
                'availableModels': availableModels,
            });
            setSelectedModelsByServiceNames({
                ...selectedModelsByServiceNames,
                [props.chatService.modelBaseNameStart]: chatServiceModel
            });
        } catch (error) {
            console.error(error);
            setLastToastMessage({
                severity: "error",
                summary: t(
                    "chat_screen.load_history_failed",
                    {
                        conversationId: conversationId,
                        interpolation: { escapeValue: false }
                    }
                ),
                detail: error.message
            });
        }
    }

    const onChatModelSelected = (model: ModelCardInterface) => {
        setSelectedModelsByServiceNames({
            ...selectedModelsByServiceNames,
            [props.chatService.modelBaseNameStart]: model
        });
    }

    /**
     * Handle changes to provider-specific options
     */
    const handleProviderOptionsChange = (options: ProviderOptionsType) => {
        setProviderOptions(prev => ({
            ...prev,
            [getCurrentProvider()]: options
        }));
    }

    return <ChatScreen
        instruction={instruction}
        setInstruction={setInstruction}
        prompt={question}
        setPrompt={setQuestion}
        images={images}
        setImages={setAndValidateImages}
        selectedTextFiles={selectedTextFiles}
        setSelectedTextFiles={setSelectedTextFiles}
        messages={messages}

        lastUsedConversationId={lastUsedConversationId}
        maxTokens={maxTokens}
        setMaxTokens={setMaxTokens}
        temperature={temperature}
        setTemperature={setTemperature}
        availableModels={availableModels}
        gptModel={selectedModelsByServiceNames[props.chatService.modelBaseNameStart]}
        extendedModelCard={extendedModelCard}
        setGptModel={onChatModelSelected}
        providerOptions={providerOptions[getCurrentProvider()] || { provider: getCurrentProvider() }}
        onProviderOptionsChange={handleProviderOptionsChange}
        completionState={completionState}

        requestNewConversation={newConversationClickHandler}
        requestForkOfChatStartingAtGivenMessage={forkMessage}
        requestContinueOfOutput={continueClickHandler}
        requestSubmitOfCurrentPrompt={submitHandler}
        requestCancelOfCurrentRequest={cancelHandler}
        requestLoadConversationFromHistory={loadConversationFromHistory}
    />
}

export default ChatScreenHoc;